import { useTranslation } from 'react-i18next';

import { TOOL_CONST } from '@/constant';
import { useAllObjectClassListQuery } from '@/hook';
import {
  Close,
  Edit,
  Info,
  VColorPicker,
  VFlex,
  VInput,
  VStack,
  VSwitch,
  VTextButton,
  VTooltip,
  cn,
} from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useTaskFrameStore,
  useToolMenuStore,
  useToolSettingStore,
} from '@/store';

import { useToolPlayControllerContext } from './ToolPlayController';

const twGroupLabel = cn('text-body-md font-semibold');
const twLabelLabel = cn('text-body-md');

const ToolSettingPopup = () => {
  const { visibleArrowHelper } = useLabelingBoxContext();
  const { $toolPlayController } = useToolPlayControllerContext();
  const {
    gridColor,
    gridShow,
    gridTextColor,
    isDirectWork,
    isFixedIntensity,
    isPcdPointColorVisible,
    isShortTrackID,
    labelBoxTrackIDVisible,
    labelingBoxOpacities,
    opacityConfig,
    orbitInfo,
    pointSize,
    pointSizeConfig,
    stepSizeConfig,

    setGridColor,
    setGridTextColor,
    setIsDirectWork,
    setLabelingBoxOpacityByProp,
    setOrbitStorageInfo,
    setPointSize,
    setStepSizeConfig,
    setToolSettingVisible,

    toggleFixedIntensity,
  } = useToolSettingStore();
  const { deselect, findByName, select } = useToolMenuStore();
  const { selectedTask } = useTaskFrameStore();
  const gridTool = findByName('grid')!;
  const shortcutPopupTool = findByName('shortcutPopup');
  const arrowHelperTool = findByName('labelBoxArrow')!;
  const isShortTrackIDToggleTool = findByName('isShortTrackID')!;
  const labelTrackIDToggleTool = findByName('labelBoxTrackID')!;
  const pcdPointColorTool = findByName('pcdPointColor')!;
  const { horizonReverse, verticalReverse } = orbitInfo;
  const { t } = useTranslation();
  const { data: enabledLabelItemList } = useAllObjectClassListQuery();

  const labelList = selectedTask?.extendClass ?? enabledLabelItemList ?? [];

  return (
    <>
      <VStack
        className="absolute left-0 h-fit w-[314px] rounded-sm bg-white p-2 shadow-sm"
        style={{
          bottom: `${$toolPlayController.current?.offsetHeight}px`,
        }}
        gap={1}
      >
        <VFlex className="w-full" items="center" justify="between">
          <div className="text-body-lg font-bold text-blue-300">Settings</div>
          <Close
            className="h-4 w-4 text-gray-400"
            role="button"
            onClick={() => {
              setToolSettingVisible(false);
              deselect(findByName('settings')!.id);
            }}
          />
        </VFlex>

        <VFlex className="w-full" gap={0.25} items="center">
          <VFlex className="flex-1" items="center" gap={0.25}>
            <div className="text-body-md font-semibold">Direct work mode</div>
            <VTooltip content={t('task.directStartTooltipText.gudget')}>
              <Info className="size-4 text-info-500" />
            </VTooltip>
          </VFlex>
          <VSwitch
            checked={isDirectWork}
            onChange={() => setIsDirectWork(!isDirectWork)}
          />
        </VFlex>

        <VFlex className="w-full" gap={0.25} items="center" justify="between">
          <VFlex className="flex-1" items="center" gap={0.25}>
            <div className="text-body-md font-semibold">
              {t('labeling.objectTrackID.daymare')}
            </div>
            <VTooltip content={t('labeling.objectTrackIDTooltipText.guider')}>
              <Info className="size-4 text-info-500" />
            </VTooltip>
          </VFlex>
          <VSwitch
            checked={labelBoxTrackIDVisible}
            onChange={() => select(labelTrackIDToggleTool.id)}
          />
        </VFlex>

        <VFlex className="w-full" gap={0.25} items="center" justify="between">
          <VFlex className="flex-1" items="center" gap={0.25}>
            <div className="text-body-md font-semibold">
              {t('labeling.isShortTrackID.retainership')}
            </div>
            <VTooltip content={t('labeling.isShortTrackIDTooltipText.palanka')}>
              <Info className="size-4 text-info-500" />
            </VTooltip>
          </VFlex>
          <VSwitch
            checked={isShortTrackID}
            onChange={() => select(isShortTrackIDToggleTool.id)}
          />
        </VFlex>

        <VFlex className="w-full" gap={0.5} items="start" justify="between">
          <VStack className="w-full" gap={0.25}>
            <VFlex
              className="w-full"
              gap={0.5}
              items="center"
              justify="between"
            >
              <div className="flex-1 text-body-md font-semibold">Grid</div>

              <div className="flex-1">
                <VColorPicker
                  fluid
                  value={gridColor}
                  onChange={e => setGridColor(e.target.value)}
                />
              </div>
            </VFlex>

            <VFlex
              className="w-full"
              gap={0.5}
              items="center"
              justify="between"
            >
              <div className="flex-1 text-body-md font-semibold">Grid Text</div>

              <div className="flex-1">
                <VColorPicker
                  fluid
                  value={gridTextColor}
                  onChange={e => setGridTextColor(e.target.value)}
                />
              </div>
            </VFlex>
          </VStack>

          <div className="relative">
            <VSwitch checked={gridShow} onChange={() => select(gridTool.id)} />
          </div>
        </VFlex>

        <VFlex gap={0.5} items="center">
          <VFlex className="flex-1" gap={0.25} items="center" justify="between">
            <div className="flex-1 text-body-md font-semibold">Intensity</div>
            <VSwitch
              checked={isFixedIntensity}
              onChange={() => toggleFixedIntensity()}
            />
          </VFlex>

          <VFlex className="flex-1" gap={0.25} items="center" justify="between">
            <div className="flex-1 text-body-md font-semibold">
              {t('tool.pcdPointColor.perilless')}
            </div>
            <VSwitch
              checked={isPcdPointColorVisible}
              onChange={() => select(pcdPointColorTool.id)}
            />
          </VFlex>
        </VFlex>

        <VFlex className="w-full" gap={0.25} items="center" justify="between">
          <VFlex className="flex-1" gap={0.25} items="center" justify="between">
            <div className="flex-1 text-body-md font-semibold">
              Direction Arrow
            </div>
            <VSwitch
              checked={visibleArrowHelper}
              onChange={() => select(arrowHelperTool.id)}
            />
          </VFlex>
        </VFlex>

        <VStack gap={0.5} className="w-full">
          <div className={twGroupLabel}>Camera control direction</div>
          <VFlex className="" gap={0.25}>
            <VFlex
              className="w-full"
              gap={0.25}
              items="center"
              justify="between"
            >
              <div className="text-body-md">Invert X-axis</div>
              <VSwitch
                checked={horizonReverse}
                onChange={() =>
                  setOrbitStorageInfo({ horizonReverse: !horizonReverse })
                }
              />
            </VFlex>

            <VFlex
              className="w-full"
              gap={0.25}
              items="center"
              justify="between"
            >
              <div className="text-body-md">Invert Y-axis</div>
              <VSwitch
                checked={verticalReverse}
                onChange={() =>
                  setOrbitStorageInfo({ verticalReverse: !verticalReverse })
                }
              />
            </VFlex>
          </VFlex>
        </VStack>

        <VStack className="w-full" gap={1}>
          <VStack gap={0.25} className="w-full">
            <div className={twGroupLabel}>PCD Point size</div>
            <VInput
              size="sm"
              label="Point"
              fluid
              value={pointSize}
              type="number"
              orientation="horizontal"
              step={pointSizeConfig.step}
              min={pointSizeConfig.min}
              max={pointSizeConfig.max}
              onChange={e => setPointSize(+e.target.value)}
            />
          </VStack>

          <VStack gap={0.25} className="w-full">
            <div className={twGroupLabel}>Step size</div>
            <VFlex gap={0.25}>
              <VInput
                size="sm"
                label="Dimension(m)"
                fluid
                value={stepSizeConfig.dimension}
                type="number"
                orientation="horizontal"
                step={TOOL_CONST.TOOL_DIMENSION_STEP_SIZE_STEP}
                min={TOOL_CONST.TOOL_DIMENSION_STEP_SIZE_MIN}
                max={TOOL_CONST.TOOL_DIMENSION_STEP_SIZE_MAX}
                onChange={e => setStepSizeConfig('dimension', +e.target.value)}
              />
              <VInput
                size="sm"
                label="Move(m)"
                fluid
                value={stepSizeConfig.move}
                type="number"
                orientation="horizontal"
                step={TOOL_CONST.TOOL_MOVE_STEP_SIZE_STEP}
                min={TOOL_CONST.TOOL_MOVE_STEP_SIZE_MIN}
                max={TOOL_CONST.TOOL_MOVE_STEP_SIZE_MAX}
                onChange={e => setStepSizeConfig('move', +e.target.value)}
              />
              <VInput
                size="sm"
                label="Rotation(°)"
                fluid
                value={stepSizeConfig.rotate}
                type="number"
                orientation="horizontal"
                step={TOOL_CONST.TOOL_ROTATE_STEP_SIZE_STEP}
                min={TOOL_CONST.TOOL_ROTATE_STEP_SIZE_MIN}
                max={TOOL_CONST.TOOL_ROTATE_STEP_SIZE_MAX}
                onChange={e => setStepSizeConfig('rotate', +e.target.value)}
              />
            </VFlex>
          </VStack>

          <VStack gap={0.25} className="w-full">
            <div className={twGroupLabel}>Box transparency</div>

            <VStack gap={0.25} className="w-full">
              {labelList.map(({ id: v, name }) => {
                const value = v;
                const opacity = labelingBoxOpacities[value];
                const background =
                  opacity?.background ??
                  TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.DEFAULT;
                const border =
                  opacity?.border ??
                  TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT;

                return (
                  <VStack className="w-full" gap={0.25} key={value}>
                    <div className={twLabelLabel}>{name}</div>
                    <VFlex gap={0.25} className="w-full" items="center">
                      <VInput
                        className="flex-1"
                        size="sm"
                        label="Surface"
                        value={background}
                        type="number"
                        orientation="horizontal"
                        step={opacityConfig.background.step}
                        min={opacityConfig.background.min}
                        max={opacityConfig.background.max}
                        onChange={e =>
                          setLabelingBoxOpacityByProp(
                            `${value}`,
                            'background',
                            +e.target.value,
                          )
                        }
                      />
                      <VInput
                        className="flex-1"
                        size="sm"
                        label="Edge"
                        value={border}
                        type="number"
                        orientation="horizontal"
                        step={opacityConfig.border.step}
                        min={opacityConfig.border.min}
                        max={opacityConfig.border.max}
                        onChange={e =>
                          setLabelingBoxOpacityByProp(
                            `${value}`,
                            'border',
                            +e.target.value,
                          )
                        }
                      />
                    </VFlex>
                  </VStack>
                );
              })}
            </VStack>
          </VStack>

          <VFlex className="w-full" justify="center">
            <VTextButton
              size="sm"
              onClick={() => select(shortcutPopupTool!.id)}
            >
              <VFlex className="w-full" items="center">
                <Edit className="size-[14px]" />
                Customize shortcuts
              </VFlex>
            </VTextButton>
          </VFlex>
        </VStack>
      </VStack>
    </>
  );
};

export default ToolSettingPopup;
